import React, { useEffect, useState } from "react";

import { useThermalPrinter } from "../hooks/useThermalPrinter";
import { prepareShippingImages, type PreparedShippingImages } from "../lib/escposShipping";
import type { ReceiptData, PrinterStatus, ShippingLabelData } from "../types/printer.types";

// ─── Props ────────────────────────────────────────────────────────────────────

type PrinterButtonProps = {
  buttonLabel: string;
  /**
   * UUID preset index (0–3). Override once you've confirmed the correct preset
   * for your VSC MP-58M Pro unit. Defaults to 0.
   */
  presetIndex?: number;
  /** Optional custom service UUID (overrides presetIndex) */
  serviceUuid?: string;
  /** Optional custom characteristic UUID (overrides presetIndex) */
  characteristicUuid?: string;
  /** Called after a successful print */
  onPrintSuccess?: () => void;
  /** Called if an error occurs during connect or print */
  onPrintError?: (err: Error) => void;
} & (
  | {
      printType: "payment_receipt";
      receipt: ReceiptData;
      shippingLabel?: never; // Explicitly forbidden when printType is payment_receipt
    }
  | {
      printType: "shipping_label";
      shippingLabel: ShippingLabelData;
      receipt?: never; // Explicitly forbidden when printType is shipping_label
    }
)

// ─── Status label helpers ──────────────────────────────────────────────────────

const STATUS_LABELS: Record<PrinterStatus, string> = {
  disconnected: "Printer tidak terhubung",
  connecting:   "Menghubungkan…",
  connected:    "Printer terhubung",
  error:        "Koneksi gagal",
};

const STATUS_COLORS: Record<PrinterStatus, string> = {
  disconnected: "#888780",
  connecting:   "#EF9F27",
  connected:    "#1D9E75",
  error:        "#E24B4A",
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Drop-in print button for your POS dashboard.
 * Handles connect → print in one click, with inline status feedback.
 */
export const PrinterButton: React.FC<PrinterButtonProps> = ({
  printType,
  receipt,
  shippingLabel,
  presetIndex,
  serviceUuid,
  characteristicUuid,
  onPrintSuccess,
  onPrintError,
  buttonLabel
}) => {
  const { connect, disconnect, printReceipt, printShippingLabel, status } = useThermalPrinter({
    presetIndex,
    serviceUuid,
    characteristicUuid,
  });

  const [isPrinting, setIsPrinting] = useState(false);
  const [lastError, setLastError]   = useState<string | null>(null);

  const [preparedImages, setPreparedImages] = useState<PreparedShippingImages | null>(null);

  useEffect(() => {
    if(printType === "shipping_label") {
      prepareShippingImages(shippingLabel).then(setPreparedImages);
    }
  }, [shippingLabel, printType]);

  const handlePrint = async (): Promise<void> => {
    setLastError(null);
    setIsPrinting(true);

    try {
      if (status !== "connected") {
        await connect();
      }
      
      if(printType === "payment_receipt") {
        await printReceipt(receipt);
      };

      if(printType === "shipping_label") {
        await printShippingLabel(shippingLabel, preparedImages ?? undefined);
      };
      
      onPrintSuccess?.();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setLastError(error.message);
      onPrintError?.(error);
    } finally {
      setIsPrinting(false);
    }
  };

  const isDisabled = isPrinting || status === "connecting";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Status indicator */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: STATUS_COLORS[status],
            display: "inline-block",
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
        <span style={{ fontSize: 13, color: "#5F5E5A" }}>
          {STATUS_LABELS[status]}
        </span>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={handlePrint}
          disabled={isDisabled}
          style={{
            padding: "8px 16px",
            fontSize: 14,
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.5 : 1,
            borderRadius: 8,
            border: "0.5px solid #888",
          }}
        >
          {isPrinting ? "Mencetak…" : `🖨️ ${buttonLabel}`}
        </button>

        {status === "connected" && !isPrinting && (
          <button
            onClick={disconnect}
            style={{
              padding: "8px 16px",
              fontSize: 14,
              cursor: "pointer",
              borderRadius: 8,
              border: "0.5px solid #888",
            }}
          >
            Putuskan
          </button>
        )}
      </div>

      {/* Error message */}
      {lastError !== null && (
        <p
          role="alert"
          style={{ fontSize: 12, color: "#E24B4A", margin: 0, maxWidth: 320 }}
        >
          {lastError}
        </p>
      )}
    </div>
  );
};
